import {PropsWithChildren} from "react";
import {createContext} from "../../../../../context/create-context";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {Button, Result} from "antd";
import {useSession, Session} from "../../../../../features/sessions";
import {GetSessionApiResponse} from "../../../../../react-query-hooks/session/types/edit-session";
import {useParams} from "react-router-dom";
import {sessionToLegacyAdapter} from "./session-to-legacy.adapter";

type GetSessionState = {
  sessionData: GetSessionApiResponse;
  session: Session | undefined;
  isFetchingSession: boolean;
  isLoadingSession: boolean;
};

type GetSessionContextState = {
  state: GetSessionState;
};

const [useContext, ContextProvider] = createContext<GetSessionContextState>();

export const useGetSessionContext = useContext;

export const GetSessionProvider = ({children}: PropsWithChildren) => {
  const {id} = useParams();
  // Convert string id to number for useSession hook
  const sessionId = id ? parseInt(id, 10) : 0;
  const getSessionQuery = useSession(sessionId, !!id && sessionId > 0);

  if (getSessionQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (getSessionQuery.isError) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching session"
        extra={
          <Button type="primary" size="small" onClick={() => getSessionQuery.refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  // Adapt Session entity to legacy GetSessionApiResponse format for consumer compatibility
  // TODO: Remove adapter in Phase 4 when consumers are updated to use Session entity directly
  const sessionData = getSessionQuery.data
    ? sessionToLegacyAdapter(getSessionQuery.data)
    : (undefined as unknown as GetSessionApiResponse);

  const state: GetSessionState = {
    sessionData,
    session: getSessionQuery.data,
    isFetchingSession: getSessionQuery.isFetching,
    isLoadingSession: getSessionQuery.isLoading,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
